import React from 'react';
import { useFormikContext, getIn } from 'formik';
import i18n from 'i18n-js';
import {
    Box,
    FormControlLabel,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
} from '@material-ui/core';

import { FormikTimePicker, Input } from 'components/shared';
import { getTakeAwayPickupRange, getTodayPickupHoursCaption } from 'helpers/orderInAdvanceHelper';
import { useStyles } from '../../../styles';
import { useAppSelector } from 'index';

export interface SameDayPickupTableProps {
    isQuantityTracked: boolean;
}

export default function SameDayPickupTable({ isQuantityTracked }: SameDayPickupTableProps) {
    const classes = useStyles();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const workingHours = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.takeAway?.workingHours
    );

    const pickupRange = getTakeAwayPickupRange(workingHours);
    const todayPickupHours = getTodayPickupHoursCaption(workingHours, pickupRange);

    const rows = [
        {
            key: 'main',
            label: values.size || values.name,
            path: 'orderInAdvanceSettings',
            settings: values.orderInAdvanceSettings,
            standardPrice: values.price,
        },
        ...values.additionalSizes.map((additionalSize: any, index: number) => ({
            key: `additional-${index}`,
            label: additionalSize.size,
            path: `additionalSizes[${index}].orderInAdvanceSettings`,
            settings: additionalSize.orderInAdvanceSettings,
            standardPrice: additionalSize.price,
        })),
    ];

    React.useEffect(() => {
        rows.forEach((row) => {
            if (row.settings?.price == null || row.settings.price === '') {
                setFieldValue(`${row.path}.price`, row.standardPrice);
            }
            if (row.settings?.pickupTime?.from == null && pickupRange.from !== null) {
                setFieldValue(`${row.path}.pickupTime.from`, pickupRange.from);
            }
            if (row.settings?.pickupTime?.to == null && pickupRange.to !== null) {
                setFieldValue(`${row.path}.pickupTime.to`, pickupRange.to);
            }
        });
    }, [
        values.price,
        values.orderInAdvanceSettings,
        values.additionalSizes,
        pickupRange.from,
        pickupRange.to,
        setFieldValue,
    ]);

    return (
        <>
            <Table className={`${classes.orderInAdvanceTable} ${classes.sameDayPickupTable}`}>
                <TableHead>
                    <TableRow>
                        <TableCell align="left">{i18n.t('common.preOrderSize')}</TableCell>
                        <TableCell align="left">{i18n.t('common.priceCHF')}</TableCell>
                        {isQuantityTracked && (
                            <TableCell align="left">{i18n.t('common.qty')}</TableCell>
                        )}
                        <TableCell align="left" style={{ padding: 10 }}>
                            {i18n.t('common.pickupHours')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const isEnabled = Boolean(row.settings?.isEnabled);
                        const pickupError = getIn(errors, `${row.path}.pickupTime`);
                        return (
                            <TableRow key={row.key}>
                                <TableCell>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                color="primary"
                                                checked={isEnabled}
                                                onChange={() =>
                                                    setFieldValue(
                                                        `${row.path}.isEnabled`,
                                                        !isEnabled
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <span
                                                className={
                                                    isEnabled
                                                        ? undefined
                                                        : classes.sameDayPickupDisabledLabel
                                                }
                                            >
                                                {row.label}
                                            </span>
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        disabled={!isEnabled}
                                        variant="filled"
                                        name={`${row.path}.price`}
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        label={i18n.t('form.price')}
                                        error={getIn(errors, `${row.path}.price`)}
                                        placeholder={i18n.t('form.price')}
                                        style={{ maxWidth: 80 }}
                                        size="small"
                                    />
                                    <Typography
                                        variant="body2"
                                        className={classes.sameDayPickupHint}
                                    >
                                        {i18n.t('common.standardPrice', {
                                            price: Number(row.standardPrice || 0).toFixed(2),
                                        })}
                                    </Typography>
                                </TableCell>
                                {isQuantityTracked && (
                                    <TableCell>
                                        <Input
                                            disabled={!isEnabled}
                                            variant="filled"
                                            name={`${row.path}.stockQuantity`}
                                            type="number"
                                            label={i18n.t('common.qty')}
                                            error={getIn(errors, `${row.path}.stockQuantity`)}
                                            placeholder={i18n.t('common.qty')}
                                            style={{ maxWidth: 80 }}
                                            size="small"
                                            inputProps={{ min: 0 }}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <FormikTimePicker
                                            name={`${row.path}.pickupTime.from`}
                                            size="small"
                                            disabled={!isEnabled}
                                            style={{ marginBottom: 0, marginRight: 5 }}
                                        />
                                        {' - '}
                                        <FormikTimePicker
                                            name={`${row.path}.pickupTime.to`}
                                            size="small"
                                            disabled={!isEnabled}
                                            style={{ marginBottom: 0, marginLeft: 5 }}
                                        />
                                    </Box>
                                    {isEnabled && typeof pickupError === 'string' && (
                                        <Typography
                                            variant="body2"
                                            className={classes.sameDayPickupError}
                                        >
                                            {pickupError}
                                        </Typography>
                                    )}
                                    {isEnabled && !pickupError && (
                                        <Typography
                                            variant="body2"
                                            className={classes.sameDayPickupHint}
                                        >
                                            {todayPickupHours ||
                                                i18n.t('common.pickupHoursPrefilledHint')}
                                        </Typography>
                                    )}
                                    {!isEnabled && (
                                        <Typography
                                            variant="body2"
                                            className={classes.sameDayPickupHint}
                                        >
                                            {i18n.t('common.sizeNotOfferedForPreOrder')}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </>
    );
}
